import { useCallback, useMemo, useRef } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useExperienceAbort } from '@jira/platform__jira-experience-tracker/src/ui/experience-abort/index.tsx';
import { useExperienceFail } from '@jira/platform__jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceStart } from '@jira/platform__jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceSuccess } from '@jira/platform__jira-experience-tracker/src/ui/experience-success/index.tsx';

const PACKAGE_NAME = 'jiraAiPlanningAiPlannerIframeModal';
const TEAM_NAME = 'dev-ai-planner';
export const AI_PLANNER_IFRAME_ANALYTICS_SOURCE = 'ai-planner';
export const AI_PLANNER_IFRAME_EXPERIENCE_TIMEOUT_MS = 15_000;

const EXPERIENCE_BY_KIND = {
	create: 'ai-planner-create-iframe-load',
	plan: 'ai-planner-plan-iframe-load',
} as const;

export type AiPlannerIframeKind = 'create' | 'plan';
export type AiPlannerIframeSurface = 'modal' | 'page';
export type AiPlannerIframeFailureReason = 'entry-point' | 'error' | 'render' | 'timeout';

export type AiPlannerIframeLoadExperience = {
	experience: 'ai-planner-create-iframe-load' | 'ai-planner-plan-iframe-load';
	experienceId: string;
	start: () => void;
	succeed: () => void;
	fail: (
		reason: AiPlannerIframeFailureReason,
		error?: Error,
		options?: { skipErrorAnalytics?: boolean },
	) => void;
	abort: (reason: string) => void;
};

const createIframeLoadError = (reason: AiPlannerIframeFailureReason): Error =>
	new Error(`AI Planner iframe load failed: ${reason}`);

export const useAiPlannerIframeLoadExperience = ({
	kind,
	surface,
}: {
	kind: AiPlannerIframeKind;
	surface: AiPlannerIframeSurface;
}): AiPlannerIframeLoadExperience => {
	const experience = EXPERIENCE_BY_KIND[kind];
	const experienceId = `${surface}-${kind}`;
	const attributes = useMemo(() => ({ iframeKind: kind, surface }), [kind, surface]);
	const startExperience = useExperienceStart({
		experience,
		experienceId,
		analyticsSource: AI_PLANNER_IFRAME_ANALYTICS_SOURCE,
		timeout: AI_PLANNER_IFRAME_EXPERIENCE_TIMEOUT_MS,
	});
	const markSucceeded = useExperienceSuccess({ experience, attributes });
	const markFailed = useExperienceFail({ experience, attributes });
	const markAborted = useExperienceAbort({ experience, attributes });
	const attemptSettledRef = useRef(false);

	const start = useCallback(() => {
		attemptSettledRef.current = false;
		startExperience();
	}, [startExperience]);

	const succeed = useCallback(() => {
		if (attemptSettledRef.current) {
			return;
		}
		attemptSettledRef.current = true;
		markSucceeded();
	}, [markSucceeded]);

	const fail = useCallback(
		(
			reason: AiPlannerIframeFailureReason,
			error = createIframeLoadError(reason),
			{ skipErrorAnalytics = false }: { skipErrorAnalytics?: boolean } = {},
		) => {
			if (attemptSettledRef.current) {
				return;
			}
			attemptSettledRef.current = true;
			markFailed(`ai-planner-iframe.${surface}`, error, { reason });
			if (skipErrorAnalytics) {
				return;
			}
			fireErrorAnalytics({
				meta: {
					id: 'iframeLoad',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				attributes: {
					...attributes,
					reason,
				},
				error,
				sendToPrivacyUnsafeSplunk: false,
			});
		},
		[attributes, markFailed, surface],
	);

	const abort = useCallback(
		(reason: string) => {
			if (attemptSettledRef.current) {
				return;
			}
			attemptSettledRef.current = true;
			markAborted(reason);
		},
		[markAborted],
	);

	return useMemo(
		() => ({ experience, experienceId, start, succeed, fail, abort }),
		[abort, experience, experienceId, fail, start, succeed],
	);
};
